import { useNavigate } from '@tanstack/react-router'
import {
  Eye,
  FileDown,
  MoreVertical,
  RotateCcw,
  Send as SendIcon,
  Trash2,
} from 'lucide-react'
import {
  Popover,
  PopoverMenu,
  PopoverMenuDivider,
  PopoverMenuItem,
  PopoverTrigger,
} from '@/components/ui/Popover/Popover'
import { toast } from '@/components/ui'
import { ApiError } from '@/lib/api/jsonFetch'
import {
  useDeleteQuote,
  useRenderQuotePdf,
  useRestoreQuote,
  useTransitionQuote,
} from '../api/queries'
import type { QuoteSummary } from '../api/types'

/**
 * Per-row ⋯ menu mirroring the CofA Management `DocumentActionsMenu`.
 * Renders in the Table view's actions column. Stops click propagation
 * so the row's open-detail click doesn't fire alongside menu actions.
 */

export interface QuoteActionsMenuProps {
  row: QuoteSummary
  onOpen: (q: QuoteSummary) => void
  onSend: (q: QuoteSummary) => void
}

export function QuoteActionsMenu({
  row, onOpen, onSend,
}: QuoteActionsMenuProps) {
  const transition = useTransitionQuote()
  const archive = useDeleteQuote()
  const restore = useRestoreQuote()
  const renderPdf = useRenderQuotePdf()
  const navigate = useNavigate()

  const pending =
    transition.isPending ||
    archive.isPending ||
    restore.isPending ||
    renderPdf.isPending

  function handleRevise() {
    // Sprint 12d — Revise navigates to the full-page editor after
    // the transition lands so the line table gets the full viewport.
    transition.mutate(
      { id: row.id, body: { to: 'draft' } },
      {
        onSuccess: () => {
          toast.success(`Reverted ${row.quote_number} to draft`)
          navigate({
            to: '/quotes/$quoteId',
            params: { quoteId: String(row.id) },
          })
        },
        onError: (err) =>
          toast.danger(
            err instanceof ApiError ? err.message : 'Revise failed',
          ),
      },
    )
  }

  function handleArchive() {
    if (!window.confirm(`Archive ${row.quote_number}?`)) return
    archive.mutate(row.id, {
      onSuccess: () => toast.success(`Archived ${row.quote_number}`),
      onError: (err) =>
        toast.danger(
          err instanceof ApiError ? err.message : 'Archive failed',
        ),
    })
  }

  function handleRestore() {
    restore.mutate(row.id, {
      onSuccess: () => toast.success(`Restored ${row.quote_number}`),
      onError: (err) =>
        toast.danger(
          err instanceof ApiError ? err.message : 'Restore failed',
        ),
    })
  }

  function handleRenderPdf() {
    renderPdf.mutate(row.id, {
      onSuccess: (data) => {
        window.open(data.download_url, '_blank')
      },
      onError: (err) =>
        toast.danger(
          err instanceof ApiError ? err.message : 'PDF render failed',
        ),
    })
  }

  const isArchived = row.deleted_at !== null
  const canRevise =
    row.status === 'revise_pricing' || row.status === 'customer_rejected'
  const canSend = row.status === 'approved'

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Row actions"
          onClick={(e) => e.stopPropagation()}
          disabled={pending}
          className="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
        >
          <MoreVertical size={14} />
        </button>
      </PopoverTrigger>
      <PopoverMenu align="end" onClick={(e) => e.stopPropagation()}>
        <PopoverMenuItem onSelect={() => onOpen(row)}>
          <Eye size={12} />
          Open
        </PopoverMenuItem>
        <PopoverMenuItem onSelect={handleRenderPdf}>
          <FileDown size={12} />
          Preview PDF
        </PopoverMenuItem>
        {canSend ? (
          <PopoverMenuItem onSelect={() => onSend(row)}>
            <SendIcon size={12} />
            Send to customer…
          </PopoverMenuItem>
        ) : null}
        {canRevise ? (
          <PopoverMenuItem onSelect={handleRevise}>
            <RotateCcw size={12} />
            Revise (back to draft)
          </PopoverMenuItem>
        ) : null}
        <PopoverMenuDivider />
        {isArchived ? (
          <PopoverMenuItem onSelect={handleRestore}>
            <RotateCcw size={12} />
            Restore from archive
          </PopoverMenuItem>
        ) : (
          <PopoverMenuItem onSelect={handleArchive}>
            <Trash2 size={12} />
            Archive
          </PopoverMenuItem>
        )}
      </PopoverMenu>
    </Popover>
  )
}
